import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { HomeWidth } from '@/components/custom/homeWidgetTypes'
import { UNMEASURED, homeFlow, sameFlow, type HomeFlow } from './homeFlow'

/** A visible card as the page holds it: its stored width and its widget's minimum, in rem. */
export interface SizedCard { id: string; width: HomeWidth; minRem: number }

/** The root element's font size in px: a rem minimum grows with the reader's own font. */
export function rootFontPx(): number {
  const px = parseFloat(getComputedStyle(document.documentElement).fontSize)
  return Number.isFinite(px) && px > 0 ? px : 16
}

/** Home's flow from the grid's own width (SA-1406), as a callback ref plus the flow — the Inbox's `usePaneLayout`
 * shape: the element is held in state, so the effect runs for the element once attached; it is measured in a layout
 * effect, so the first painted frame is already right; and a ResizeObserver follows it. The last width is kept, so a
 * Customize change (no resize) recomputes too, without the tolerance band: the band is for a scrollbar that comes and
 * goes, and undoing a width change must bring the earlier stage back. Only the flow is stored, and only when it moves,
 * so a resize re-renders the page when it crosses a threshold and never on each pixel. Unattached reads as unmeasured.
 * `latest()` is the flow the hook last worked out, which runs one render ahead of `flow` while a change is on its way:
 * a page effect that must wait for the new stage to be drawn compares the two. */
export function useHomeFlow(cards: SizedCard[]): [(el: HTMLDivElement | null) => void, HomeFlow, () => HomeFlow] {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [flow, setFlow] = useState<HomeFlow>(UNMEASURED)
  const widthRef = useRef(0)
  const flowRef = useRef<HomeFlow>(UNMEASURED)
  const cardsRef = useRef(cards)

  const apply = useCallback((banded: boolean) => {
    const font = rootFontPx()
    const sized = cardsRef.current.map((c) => ({ id: c.id, width: c.width, minPx: c.minRem * font }))
    const next = homeFlow(widthRef.current, sized, banded ? flowRef.current : null)
    if (sameFlow(next, flowRef.current)) return
    flowRef.current = next
    setFlow(next)
  }, [])

  useLayoutEffect(() => {
    cardsRef.current = cards
    apply(false)
  }, [cards, apply])

  useLayoutEffect(() => {
    widthRef.current = el ? el.getBoundingClientRect().width : 0
    apply(true)
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (typeof w !== 'number') return
      widthRef.current = w
      apply(true)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [el, apply])

  const latest = useCallback(() => flowRef.current, [])

  return [setEl, flow, latest]
}
